import React, { useState, useMemo, useCallback } from "react";
import moment from "moment";
import { FiDownload, FiFilter, FiChevronDown, FiChevronUp } from "react-icons/fi";

import { DateNavigation, ViewToggle } from "../../../components/Fixtures";
import SharedEmptyState from "../../../components/EmptyState";
import emptyStateImage from "../../../assets/images/fixtures-empty-state.svg";
import TeamBadge from "../../../components/TeamBadge/TeamBadge";

const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  return `${hr === 0 ? 12 : hr > 12 ? hr - 12 : hr}:${m || "00"} ${hr >= 12 ? "PM" : "AM"}`;
};

const ResultsCalendarView = ({
  fixturesByDate,
  sortedDates,
  selectedTournament,
  selectedCategory,
  startDate,
  endDate,
  onDateChange,
  onShiftDates,
  isCalendarView,
  onToggleView,
  onOpenFilters,
  activeFiltersCount = 0,
  onAddResult,
  onEditFixture,
  getMatchTeams,
  showConflicts = false,
  lockedPhaseIds = new Set(),
  isOwner = false,
}) => {
  const [collapsedDates, setCollapsedDates] = useState({});

  const toggleDateCollapse = useCallback((date) => {
    setCollapsedDates(prev => ({ ...prev, [date]: !prev[date] }));
  }, []);

  // Generate time slots (9 AM to 8 PM, hourly)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 9; hour <= 20; hour++) {
      slots.push({
        hour,
        label: hour === 12 ? "12 PM" : hour < 12 ? `${hour} AM` : `${hour - 12} PM`,
        value: hour,
      });
    }
    return slots;
  }, []);

  // Get pitches from tournament settings, plus any fixture-only pitches
  const pitches = useMemo(() => {
    const pitchSet = new Set();

    // Start with tournament-configured pitches
    if (selectedTournament?.matchVenue?.pitches) {
      selectedTournament.matchVenue.pitches.forEach((pitch) => {
        if (typeof pitch === "string") pitchSet.add(pitch);
        else if (pitch?.name) pitchSet.add(pitch.name);
        else if (pitch?.label) pitchSet.add(pitch.label);
      });
    }

    // Also include pitches from fixtures (handles legacy/renamed pitches)
    if (pitchSet.size === 0) {
      Object.values(fixturesByDate).forEach((fixtures) => {
        fixtures.forEach((fixture) => {
          const pitch = fixture.pitch || fixture.pitchName || fixture.pitch_name;
          if (pitch) pitchSet.add(pitch);
        });
      });
    }

    if (pitchSet.size === 0 && Object.values(fixturesByDate).flat().length > 0) {
      pitchSet.add("Unassigned");
    }

    return Array.from(pitchSet).sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, "")) || 0;
      const numB = parseInt(b.replace(/\D/g, "")) || 0;
      if (numA !== 0 && numB !== 0) return numA - numB;
      return a.localeCompare(b);
    });
  }, [fixturesByDate, selectedTournament]);

  const buildGrid = useCallback(
    (dateFixtures) => {
      const grid = {};
      timeSlots.forEach((slot) => {
        grid[slot.hour] = {};
        pitches.forEach((pitch) => { grid[slot.hour][pitch] = []; });
      });

      dateFixtures.forEach((fixture) => {
        const matchTimeStr = fixture.matchTime
          ? moment(fixture.matchTime).format("HH:mm")
          : fixture.match_time || fixture.time;
        let pitch = fixture.pitch || fixture.pitchName || fixture.pitch_name;
        if (!pitch && pitches.includes("Unassigned")) pitch = "Unassigned";

        if (matchTimeStr && pitch) {
          const hour24 = parseInt(String(matchTimeStr).split(":")[0]) || 0;
          let matchedSlot = timeSlots.find((s) => s.value === hour24);
          if (!matchedSlot && hour24 >= 9 && hour24 <= 20) {
            matchedSlot = timeSlots.reduce((closest, slot) =>
              Math.abs(slot.value - hour24) < Math.abs(closest.value - hour24) ? slot : closest
            );
          }
          if (matchedSlot) {
            const resolvedPitch = grid[matchedSlot.hour]?.[pitch] ? pitch : pitches[0];
            if (resolvedPitch && grid[matchedSlot.hour]?.[resolvedPitch]) {
              grid[matchedSlot.hour][resolvedPitch].push(fixture);
            }
          }
        }
      });

      return grid;
    },
    [timeSlots, pitches]
  );

  const exportToCSV = () => {
    const headers = ["Date", "Time", "Home Team", "Away Team", "Pitch", "Phase", "Group"];
    const rows = [];
    Object.entries(fixturesByDate).forEach(([date, fixtures]) => {
      fixtures.forEach((fixture) => {
        const startMoment = fixture.matchTime ? moment(fixture.matchTime) : null;
        rows.push([
          startMoment ? startMoment.format("YYYY-MM-DD") : (date || ""),
          startMoment ? startMoment.format("HH:mm") : "",
          fixture.homeTeam?.title || fixture.homeTeam?.name || "TBD",
          fixture.awayTeam?.title || fixture.awayTeam?.name || "TBD",
          fixture.pitch || "",
          fixture.phaseName || "",
          fixture.groupName || "",
        ]);
      });
    });
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell ?? "")}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `results-${selectedTournament?.name || "export"}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Date Navigation & Actions Row */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:h-[44px] mb-4">
        <DateNavigation
          startDate={startDate ? new Date(startDate) : null}
          endDate={endDate ? new Date(endDate) : null}
          onDateChange={(start, end) => {
            if (start) onDateChange?.("start", moment(start).format("YYYY-MM-DD"));
            if (end) onDateChange?.("end", moment(end).format("YYYY-MM-DD"));
          }}
          onShiftDates={onShiftDates}
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={exportToCSV}
            className="w-11 h-11 bg-app-card rounded-full flex items-center justify-center hover:opacity-50 transition-opacity"
            title="Download CSV"
          >
            <FiDownload className="w-5 h-5 text-[#FEFCEB]" />
          </button>
          <button
            onClick={onOpenFilters}
            className="h-11 px-4 bg-app-card rounded-full flex items-center gap-2 hover:opacity-50 transition-opacity"
          >
            <FiFilter className="w-5 h-5 text-[#FEFCEB]" />
            <span className="text-[#FEFCEB] font-medium text-[15px]">
              Filters{activeFiltersCount > 0 && ` (${activeFiltersCount})`}
            </span>
          </button>
          <ViewToggle isCalendar={isCalendarView} onToggle={onToggleView} />
        </div>
      </div>

      {/* Date Sections */}
      {sortedDates.length === 0 ? (
        <SharedEmptyState
          illustration={emptyStateImage}
          heading="No fixtures yet"
          description="Get started quickly with auto-generated fixtures, or create your own manually in the fixtures tab."
        />
      ) : (
        <div className="space-y-4 mt-4">
          {sortedDates.map((date) => {
            const dateFixtures = fixturesByDate[date] || [];
            const isCollapsed = collapsedDates[date];
            const grid = isCollapsed ? null : buildGrid(dateFixtures);

            return (
              <div key={date} className="bg-app-surface p-3 rounded-2xl">
                {/* Collapsible heading */}
                <div
                  className="flex items-center gap-2 cursor-pointer select-none"
                  onClick={() => toggleDateCollapse(date)}
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
                    {isCollapsed ? (
                      <FiChevronDown className="w-5 h-5 text-[#FEFCEB]" />
                    ) : (
                      <FiChevronUp className="w-5 h-5 text-[#FEFCEB]" />
                    )}
                  </div>
                  <h3 className="text-[#FEFCEB] text-[16px] font-medium m-0">
                    {moment(date).format("ddd, D MMM")}
                  </h3>
                </div>

                {/* Calendar Grid */}
                {!isCollapsed && pitches.length > 0 && (
                  <div className="bg-[#000F1F] border border-[#192634] rounded-[20px] overflow-x-auto mt-4">
                    <div style={{ minWidth: `${80 + pitches.length * 280}px` }}>
                      {/* Grid Header — Pitches */}
                      <div
                        className="grid border-b border-[#192634]"
                        style={{ gridTemplateColumns: `80px repeat(${pitches.length}, 1fr)` }}
                      >
                        <div className="p-3 border-r border-[#192634]" />
                        {pitches.map((pitch) => (
                          <div key={pitch} className="p-3 border-r border-[#192634] last:border-r-0 text-center">
                            <h3 className="text-[#FEFCEB] text-[14px] font-semibold m-0">{pitch}</h3>
                          </div>
                        ))}
                      </div>

                      {/* Grid Body — Time Slots */}
                      {timeSlots.map((slot) => (
                        <div
                          key={slot.hour}
                          className="grid border-b border-[#192634] last:border-b-0 min-h-[100px]"
                          style={{ gridTemplateColumns: `80px repeat(${pitches.length}, 1fr)` }}
                        >
                          <div className="p-3 border-r border-[#192634] flex items-center">
                            <span className="text-[#FEFCEB] text-[14px] font-semibold">{slot.label}</span>
                          </div>
                          {pitches.map((pitch) => {
                            const fixtures = grid[slot.hour]?.[pitch] || [];
                            return (
                              <div
                                key={pitch}
                                className="p-1.5 border-r border-[#192634] last:border-r-0 min-h-[100px] flex flex-col"
                              >
                                {fixtures.map((fixture) => {
                                  const matchTeams = getMatchTeams?.(fixture) || { homeTeam: {}, awayTeam: {} };
                                  const home = { ...fixture.homeTeam, ...matchTeams.homeTeam };
                                  const away = { ...fixture.awayTeam, ...matchTeams.awayTeam };
                                  const isFinal = fixture.status === "COMPLETED" || fixture.hasResult;
                                  const isPending = !isFinal;
                                  const homeTitle = home.title || home.name || "TBD";
                                  const awayTitle = away.title || away.name || "TBD";
                                  const phaseName = fixture.phaseName || "Phase";
                                  const groupName = fixture.groupName || "";

                                  return (
                                    <div
                                      key={fixture._id || fixture.id}
                                      className="bg-[#000F1F] border border-[#192634] rounded-2xl mb-1.5 last:mb-0 flex flex-col hover:border-[#00E7FF]/50 transition-colors overflow-hidden"
                                    >
                                      {/* Top bar — phase/group */}
                                      <div className="px-2">
                                        <span className="text-[9px] text-app-text-secondary truncate">
                                          {phaseName}{groupName ? ` - ${groupName}` : ""}
                                        </span>
                                      </div>

                                      {/* Teams + Score/Time */}
                                      <div className="flex items-center justify-between px-2 py-1.5">
                                        <div className="flex flex-col gap-1 items-center justify-center w-[72px] shrink-0">
                                          <TeamBadge src={home.crest || home.logo} alt={homeTitle} containerSize="w-9 h-9" size="w-3 h-3" />
                                          <p className="text-[#FEFCEB] text-[10px] font-semibold text-center leading-tight m-0 w-full min-w-0 truncate">{homeTitle}</p>
                                        </div>
                                        <div className="flex flex-col items-center justify-center flex-1 min-w-[36px]">
                                          {isFinal ? (
                                            <p className="text-[#FEFCEB] text-sm font-bold m-0">{fixture.homeTeamScore ?? 0} - {fixture.awayTeamScore ?? 0}</p>
                                          ) : (
                                            <div className="text-center">
                                              <p className="text-[#FEFCEB] text-[10px] font-medium m-0">VS</p>
                                              <p className="text-app-text text-[10px] m-0 whitespace-nowrap">{formatTime(fixture.matchTime ? moment(fixture.matchTime).format("HH:mm") : "")}</p>
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex flex-col gap-1 items-center justify-center w-[72px] shrink-0">
                                          <TeamBadge src={away.crest || away.logo} alt={awayTitle} containerSize="w-9 h-9" size="w-3 h-3" />
                                          <p className="text-[#FEFCEB] text-[10px] font-semibold text-center leading-tight m-0 w-full min-w-0 truncate">{awayTitle}</p>
                                        </div>
                                      </div>

                                      {/* Action button — hidden when phase is locked, unless owner */}
                                      {(!lockedPhaseIds.has(fixture.phaseId) || isOwner) && (
                                        <div className="flex justify-center items-center w-full">
                                          <button
                                            onClick={() => onAddResult?.(fixture)}
                                            className={`w-[100%] ${pitches?.length < 3 && "max-w-[90px]"} mx-1.5 mb-1.5 py-1.5 bg-app-card text-[#FEFCEB] rounded-full hover:opacity-50 transition-opacity font-medium text-[11px]`}
                                          >
                                            {isPending ? "Add result" : "Edit result"}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ResultsCalendarView;
